import { useRef } from 'react';
import { QueryClient, keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';
import { api, ApiFailure } from './client';
import type { CellDetail, IsoDate, ScoresResponse, Species, TimelineResponse } from './contract';
import { friseDays } from '../lib/dates';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // La carte change une fois par jour, au calcul de 6 h.
      staleTime: 30 * 60 * 1000,
      gcTime: 24 * 60 * 60 * 1000,
      retry: (count, error) => !(error instanceof ApiFailure && (error.offline || error.status < 500)) && count < 2,
      refetchOnWindowFocus: false,
    },
  },
});

export type BySpecies<T> = Partial<Record<Species, T>>;
type Bbox = [number, number, number, number];

/** Rassemble des requêtes par espèce en un objet { cepe, girolle }. */
function bySpecies<T>(shown: Species[], results: { data?: T | undefined }[]): BySpecies<T> {
  const out: BySpecies<T> = {};
  shown.forEach((s, i) => {
    const data = results[i]?.data;
    if (data) out[s] = data;
  });
  return out;
}

export const useZone = () => useQuery({ queryKey: ['zone'], queryFn: api.zone, staleTime: Infinity });

/** Mailles de 70 ha de toute la zone, pour chaque espèce affichée. */
export function useScores(shown: Species[], date: IsoDate | null): BySpecies<ScoresResponse> {
  const results = useQueries({
    queries: shown.map((species) => ({
      queryKey: ['scores', species, date],
      queryFn: () => api.scores(species, date!),
      enabled: date != null,
      // Pendant le glissement sur la frise, la carte garde le jour précédent jusqu'à l'arrivée du suivant.
      placeholderData: keepPreviousData,
    })),
  });
  return bySpecies(shown, results);
}

/** Emprise arrondie vers l'extérieur sur une grille : de petits déplacements réutilisent la même réponse. */
const snapBbox = (b: Bbox, step: number): Bbox => [
  Math.floor(b[0] / step) * step,
  Math.floor(b[1] / step) * step,
  Math.ceil(b[2] / step) * step,
  Math.ceil(b[3] / step) * step,
];

/** Sous-mailles (résolution 9 ou 10) de l'emprise visible, au zoom proche. */
export function useFineScores(shown: Species[], date: IsoDate | null, resolution: number | null, bbox: Bbox | null) {
  const snapped = bbox && resolution ? snapBbox(bbox, resolution === 9 ? 0.05 : 0.02) : null;
  const results = useQueries({
    queries: shown.map((species) => ({
      queryKey: ['scores-fines', species, date, resolution, snapped?.join(',')],
      queryFn: () => api.scores(species, date!, { resolution: resolution!, bbox: snapped! }),
      enabled: date != null && snapped != null,
      placeholderData: keepPreviousData,
    })),
  });
  const data = bySpecies(shown, results);
  // Les données gardées d'une autre résolution ne doivent pas se mélanger à la nouvelle.
  for (const s of shown) if (data[s] && data[s]!.resolution !== resolution) delete data[s];
  return resolution ? data : {};
}

export function useCells(h3: string | null, shown: Species[], date: IsoDate | null) {
  const results = useQueries({
    queries: shown.map((species) => ({
      queryKey: ['cell', h3, species, date],
      queryFn: () => api.cell(h3!, species, date!),
      enabled: h3 != null && date != null,
    })),
  });
  // Changer de jour change la clé : useQueries repart d'un observateur sans données, et la fiche se viderait
  // le temps du chargement (le panneau remonterait en haut). On garde la dernière fiche de la même maille.
  const last = useRef<{ h3: string | null; data: BySpecies<CellDetail> }>({ h3: null, data: {} });
  const fresh = bySpecies(shown, results);
  if (last.current.h3 !== h3) last.current = { h3, data: {} };
  Object.assign(last.current.data, fresh);
  const data: BySpecies<CellDetail> = {};
  shown.forEach((s, i) => {
    // Une erreur sur le nouveau jour ne doit pas laisser l'ancien affiché comme s'il était à jour.
    const d = fresh[s] ?? (results[i]?.isError ? undefined : last.current.data[s]);
    if (d) data[s] = d;
  });
  const error = results.find((r) => r.error)?.error;
  return {
    data,
    loading: results.some((r) => r.isFetching),
    failed: results.some((r) => r.isError),
    /** Code d'erreur de l'API (hors_foret, hors_zone, hors_ligne…), pour un message juste. */
    errorCode: error instanceof ApiFailure ? error.code : null,
  };
}

/** Emprise arrondie pour que de petits déplacements réutilisent la même réponse. */
export function useTimelines(shown: Species[], bbox: Bbox | null): BySpecies<TimelineResponse> {
  const rounded = bbox ? snapBbox(bbox, 0.02) : null;
  const results = useQueries({
    queries: shown.map((species) => ({
      queryKey: ['timeline', species, rounded?.join(',')],
      queryFn: () => api.timeline(species, rounded!),
      enabled: rounded != null,
      placeholderData: keepPreviousData,
    })),
  });
  return bySpecies(shown, results);
}

/**
 * Cartes des jours voisins du jour affiché, chargées d'avance pour que le glissement sur la frise reste fluide :
 * une semaine de part et d'autre, la fenêtre suit le doigt.
 */
export function useFriseScores(shown: Species[], today: IsoDate | null, date: IsoDate | null, enabled: boolean) {
  const days = today && enabled ? friseDays(today) : [];
  const i = Math.max(0, days.indexOf(date ?? today ?? ''));
  const near = days.slice(Math.max(0, i - 7), i + 8);
  return useQueries({
    queries: near.flatMap((d) =>
      shown.map((species) => ({
        queryKey: ['scores', species, d],
        queryFn: () => api.scores(species, d),
      })),
    ),
  });
}
